import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
} from "@elaniintc/goes-design-system";
import { VariableSelector } from "./VariableSelector";
import { OperationSelector } from "./OperationSelector";
import { ComparativeValueInput } from "./ComparativeValueInput";
import { ComparativeValueInputArray } from "./ComparativeValueInput.array";
import { getLogicalOperatorName, parseArrayValue } from "./utils";
import type {
  Variable,
  Operation,
  LogicalOperator,
  ComparativeValueType,
} from "./types";
import type { NodeVariable } from "../types";

interface SubConditionProps {
  id: string;
  mainVariableType: string;
  variables: NodeVariable[];
  variable: Variable | null;
  operation: Operation | null;
  logicalOperator: LogicalOperator;
  comparativeValue: Variable | string | string[];
  comparativeValueType: ComparativeValueType;
  setComparativeValue: (value: string | Variable | string[]) => void;
  setComparativeValueType: (type: ComparativeValueType) => void;
  onSelectVariable: (val: Variable) => void;
  onSelectOperation: (op: Operation | null) => void;
  setLogicalOperator: (operator: LogicalOperator) => void;
  onDelete: () => void;
}

export const SubCondition = ({
  id,
  variable,
  variables,
  operation,
  logicalOperator,
  comparativeValue,
  comparativeValueType,
  setComparativeValue,
  onSelectVariable,
  onSelectOperation,
  onDelete,
  setLogicalOperator,
  setComparativeValueType,
}: SubConditionProps) => {
  return (
    <div className="w-full grid grid-cols-[1fr_32px] place-items-center gap-2">
      <GroupTextField label="" className="w-full min-w-0" key={id}>
        <GroupTextFieldRow>
          <div className="w-full flex">
            <Button
              variant="quiet"
              className="h-full text-body-loud shrink-0"
              group={
                operation?.needsComparativeValue &&
                operation?.comparativeValueType !== "array"
                  ? "up-left"
                  : "left"
              }
              onClick={() => {
                let newOperator: LogicalOperator = "and";
                if (logicalOperator === "and") {
                  newOperator = "or";
                } else if (logicalOperator === "or") {
                  newOperator = "not";
                } else {
                  newOperator = "and";
                }
                setLogicalOperator(newOperator);
              }}
            >
              {getLogicalOperatorName(logicalOperator)}
            </Button>
            <VariableSelector
              variables={variables}
              selectedVariable={variable}
              onSelectVariable={(selectedVar) => {
                if (selectedVar) {
                  onSelectVariable(selectedVar);
                }
              }}
              triggerGroup="middle"
              className="border-t border-t-neutral-loud"
            />
            <OperationSelector
              selectedVariable={variable}
              selectedOperation={operation}
              onSelectOperation={onSelectOperation}
              group={
                operation?.needsComparativeValue &&
                operation?.comparativeValueType !== "array"
                  ? "up-right"
                  : "right"
              }
              disabled={!variable}
            />
          </div>
        </GroupTextFieldRow>
        {operation?.needsComparativeValue &&
          (operation?.comparativeValueType === "array" ? (
            <GroupTextFieldRow className="h-auto">
              <ComparativeValueInputArray
                variables={variables}
                value={parseArrayValue(comparativeValue)}
                onChange={(newValue) => setComparativeValue(newValue)}
              />
            </GroupTextFieldRow>
          ) : (
            <GroupTextFieldRow>
              <Button
                variant="quiet"
                type="button"
                className="font-mono"
                data-testid="toggle-comparative-value-type-btn"
                onClick={() => {
                  setComparativeValueType(
                    comparativeValueType === "input" ? "variable" : "input",
                  );
                  setComparativeValue("");
                }}
              >
                {comparativeValueType === "input" ? "inp" : "var"}
              </Button>
              <ComparativeValueInput
                selectedVariable={variable}
                selectedOperation={{
                  ...operation,
                  comparativeValueType:
                    comparativeValueType === "variable"
                      ? "variable"
                      : operation.comparativeValueType,
                }}
                comparativeValue={comparativeValue}
                onValueChange={setComparativeValue}
                variables={variables}
              />
            </GroupTextFieldRow>
          ))}
      </GroupTextField>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Eliminar condición"
        className="size-7"
        type="button"
        onClick={onDelete}
      >
        <Icon type="far" name="times" />
      </Button>
    </div>
  );
};
